import { DINE_IN } from "@/config/const";

/**
 * Cart subtotal: member_price when logged in, price_default otherwise
 * (same rule as calculateTotal() on MyCart / kiosk Checkout).
 */
export const cartSubtotal = (items, useMemberPrice) =>
  (items ?? []).reduce((total, product) => {
    const price = useMemberPrice ? product.member_price : product.price_default;
    return total + (price ? price * product.quantity : 0);
  }, 0);

/**
 * BCRS deposit of the cart — same counting as totalBcrs on MyCart / kiosk Checkout:
 * main item, choice items, modifiers and sub modifiers flagged eligible_bcrs.
 */
export const cartBcrs = (items, branch) => {
  if (!(parseInt(branch?.enable_bcrs) || 0)) return 0;

  const eachBcrsAmount = parseFloat(branch.each_bcrs_amount) || 0;
  if (eachBcrsAmount <= 0) return 0;

  // Quantity of eligible modifiers (and their sub modifiers) in a modifier_group list.
  const modifierQty = (groups) =>
    (groups ?? []).reduce((sum, group) => {
      (group.items ?? []).forEach((modifier) => {
        const modQty = parseInt(modifier.quantity) || 0;
        if (parseInt(modifier.eligible_bcrs) === 1) sum += modQty;
        sum += modQty * modifierQty(modifier.item_in_mog?.modifier_group);
      });
      return sum;
    }, 0);

  let totalQty = 0;

  (items ?? []).forEach((item) => {
    if (!item || item.is_delivery_fee_item) return;

    const mainQty = parseInt(item.quantity) || 0;
    if (mainQty <= 0) return;

    if (parseInt(item.eligible_bcrs) === 1) totalQty += mainQty;

    if (item.choice) {
      (item.choice_selected?.items ?? []).forEach((itemChoice) => {
        const choiceQty = parseInt(itemChoice.qty) || 0;
        if (choiceQty <= 0) return;
        if (parseInt(itemChoice.item_detail?.eligible_bcrs) === 1) {
          totalQty += choiceQty * mainQty;
        }
        totalQty += modifierQty(itemChoice.item_detail?.modifier_group) * choiceQty * mainQty;
      });
    } else {
      totalQty += modifierQty(item.modifier_group) * mainQty;
    }
  });

  return totalQty * eachBcrsAmount;
};

/**
 * Total payable of the cart as the cart / checkout screen shows it before a
 * member voucher (priced by the API) is applied:
 * subtotal − promo, + service charge (dine-in), + GST when exclusive, + BCRS.
 *
 * - promoApplied / promoDiscount: whether a promo code is on the cart and the
 *   caller's figure for it (MOO rounds it, KIOSK does not).
 * - serviceChargeOnDiscounted: SF230 branch toggle on MOO; KIOSK always true.
 * - GST is rounded to the cent without a promo and left unrounded with one,
 *   as calculateTotalAfterGST() / calculateFinalTotalWithPromoCode() do.
 */
export const cartTotalPayable = ({
  items,
  branch,
  diningOption,
  useMemberPrice,
  promoApplied = false,
  promoDiscount = 0,
  serviceChargeOnDiscounted = true,
}) => {
  if (!items || items.length === 0) return 0;
  // Branch not cached yet: no fee settings known, show the items alone.
  if (!branch) return cartSubtotal(items, useMemberPrice);

  const subtotal = cartSubtotal(items, useMemberPrice);
  let amount = Math.max(0, subtotal - promoDiscount);

  const serviceChargeRate = parseFloat(branch.service_charge_rate) || 0;
  if (diningOption == DINE_IN && serviceChargeRate > 0) {
    const base = serviceChargeOnDiscounted ? amount : subtotal;
    amount += base * (serviceChargeRate / 100);
  }

  const gstRate = parseFloat(branch.gst_rate);
  if (gstRate > 0) {
    const gst = amount * (gstRate / 100);
    amount += promoApplied
      ? gst
      : Math.round((gst + Number.EPSILON) * 100) / 100;
  }

  return Math.max(0, amount) + cartBcrs(items, branch);
};
